import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  getInvoices, updateInvoice, getInvoicePdfUrl, startInvoiceDunning, sendDunningNow,
} from '../../services/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../ui/dropdown-menu';
import {
  FileText, Search, MoreVertical, Send, Download, X as XIcon, Users, Receipt, Clock, AlertTriangle, Wallet, Mail,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  StatCard, FilterChip, FilterRow, FiltersCard, SurfaceCard, ResetFiltersButton,
} from '../ui/filters-toolkit';

const fmt = (n) => new Intl.NumberFormat('es-ES', { useGrouping: 'always', style: 'currency', currency: 'EUR' }).format(n || 0);

const STATUS_OPTIONS = [
  { value: 'sent', label: 'Enviada', color: 'bg-blue-100 text-blue-700' },
  { value: 'paid', label: 'Pagada', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'overdue', label: 'Vencida', color: 'bg-red-100 text-red-700' },
];

const STATUS_CHIPS = [
  { value: 'all', label: 'Todas pendientes', dot: 'bg-stone-400' },
  { value: 'sent', label: 'Enviadas', dot: 'bg-blue-500' },
  { value: 'overdue', label: 'Vencidas', dot: 'bg-red-500' },
];

const QUARTERS = [
  { value: 'all', label: 'Todo el año' },
  { value: 'Q1', label: 'T1 · Ene-Mar' },
  { value: 'Q2', label: 'T2 · Abr-Jun' },
  { value: 'Q3', label: 'T3 · Jul-Sep' },
  { value: 'Q4', label: 'T4 · Oct-Dic' },
];

const getYear = (iso) => {
  if (!iso) return null;
  const y = parseInt(iso.slice(0, 4), 10);
  return Number.isFinite(y) ? y : null;
};
const getQuarter = (iso) => {
  if (!iso) return null;
  const m = parseInt(iso.slice(5, 7), 10);
  if (!Number.isFinite(m)) return null;
  if (m <= 3) return 'Q1';
  if (m <= 6) return 'Q2';
  if (m <= 9) return 'Q3';
  return 'Q4';
};

const daysSince = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / 86400000);
};

/**
 * Pending-invoices list, with the SAME look/filters/functions as InvoicesPage
 * but pre-filtered to status in ['sent', 'overdue'] so the user works the
 * receivables backlog without leaving the "Por Cobrar" module.
 */
export const PendingInvoicesList = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [yearFilter, setYearFilter] = useState('all');
  const [quarterFilter, setQuarterFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('all');
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await getInvoices();
      // Only pending receivables: sent or overdue. Drafts/paid/cancelled out.
      setInvoices((data || []).filter((i) => i.status === 'sent' || i.status === 'overdue'));
    } catch {
      toast.error('Error cargando facturas pendientes');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const years = useMemo(() => {
    const set = new Set(invoices.map((i) => getYear(i.issue_date)).filter(Boolean));
    return Array.from(set).sort((a, b) => b - a);
  }, [invoices]);

  const clients = useMemo(() => {
    const set = new Set(invoices.map((i) => i.client_name).filter(Boolean));
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'es'));
  }, [invoices]);

  const filtered = useMemo(() => {
    const q = searchTerm.toLowerCase().trim();
    return invoices.filter((inv) => {
      if (q) {
        const hay = `${inv.invoice_number || ''} ${inv.client_name || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (statusFilter !== 'all' && inv.status !== statusFilter) return false;
      if (yearFilter !== 'all' && getYear(inv.issue_date) !== parseInt(yearFilter, 10)) return false;
      if (quarterFilter !== 'all' && getQuarter(inv.issue_date) !== quarterFilter) return false;
      if (clientFilter !== 'all' && inv.client_name !== clientFilter) return false;
      return true;
    });
  }, [invoices, searchTerm, statusFilter, yearFilter, quarterFilter, clientFilter]);

  const stats = useMemo(() => {
    const acc = { pendiente: 0, vencidas: 0, vencidasCount: 0, masVencido: 0, count: filtered.length };
    filtered.forEach((inv) => {
      const t = Number(inv.total) || 0;
      acc.pendiente += t;
      if (inv.status === 'overdue') {
        acc.vencidas += t;
        acc.vencidasCount += 1;
        const d = daysSince(inv.due_date) || 0;
        if (d > acc.masVencido) acc.masVencido = d;
      }
    });
    return acc;
  }, [filtered]);

  const activeFiltersCount = [
    statusFilter !== 'all', yearFilter !== 'all', quarterFilter !== 'all',
    clientFilter !== 'all', searchTerm.trim() !== '',
  ].filter(Boolean).length;

  const resetFilters = () => {
    setSearchTerm(''); setStatusFilter('all'); setYearFilter('all');
    setQuarterFilter('all'); setClientFilter('all');
  };

  const allSelected = filtered.length > 0 && filtered.every((i) => selected.has(i.id));
  const toggleSelectAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(filtered.map((i) => i.id)));
  };
  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await updateInvoice(id, { status: newStatus });
      toast.success(`Estado: ${STATUS_OPTIONS.find((o) => o.value === newStatus)?.label || newStatus}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al actualizar');
    }
  };

  const handleMarkPaid = async (id) => {
    if (!window.confirm('¿Marcar esta factura como pagada?')) return;
    await handleStatusChange(id, 'paid');
  };

  const handleSendReminder = async (id) => {
    try {
      try { await startInvoiceDunning(id); } catch (_) {}
      await sendDunningNow(id, { step_index: 0 });
      toast.success('Recordatorio enviado');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo enviar el recordatorio');
    }
  };

  const handleBulkRemind = async () => {
    setBusy(true);
    let ok = 0; let err = 0;
    for (const id of selected) {
      try {
        try { await startInvoiceDunning(id); } catch (_) {}
        await sendDunningNow(id, { step_index: 0 });
        ok++;
      } catch { err++; }
    }
    setBusy(false);
    setSelected(new Set());
    if (ok) toast.success(`${ok} recordatorio${ok === 1 ? '' : 's'} enviado${ok === 1 ? '' : 's'}`);
    if (err) toast.error(`${err} fallaron`);
  };

  const handleBulkMarkPaid = async () => {
    if (!window.confirm(`¿Marcar ${selected.size} factura${selected.size > 1 ? 's' : ''} como pagada${selected.size > 1 ? 's' : ''}?`)) return;
    setBusy(true);
    let ok = 0; let err = 0;
    for (const id of selected) {
      try { await updateInvoice(id, { status: 'paid' }); ok++; } catch { err++; }
    }
    setBusy(false);
    setSelected(new Set());
    if (ok) toast.success(`${ok} factura${ok === 1 ? '' : 's'} marcada${ok === 1 ? '' : 's'} como pagada${ok === 1 ? '' : 's'}`);
    if (err) toast.error(`${err} fallaron`);
    load();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6B4A]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stat cards (pending-focused) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="pending-stats">
        <StatCard
          label="Pendiente total"
          value={fmt(stats.pendiente)}
          sub={`${stats.count} factura${stats.count === 1 ? '' : 's'}`}
          icon={Receipt}
          gradient="bg-gradient-to-br from-orange-50 to-amber-50"
          iconBg="bg-gradient-to-br from-[#FF6B4A] to-[#FBBF24]"
          testid="stat-pending-total"
        />
        <StatCard
          label="Enviadas"
          value={fmt(stats.pendiente - stats.vencidas)}
          sub={`${stats.count - stats.vencidasCount} factura${stats.count - stats.vencidasCount === 1 ? '' : 's'}`}
          icon={Clock}
          gradient="bg-gradient-to-br from-sky-50 to-blue-50"
          iconBg="bg-gradient-to-br from-sky-500 to-blue-500"
          testid="stat-pending-sent"
        />
        <StatCard
          label="Vencidas"
          value={fmt(stats.vencidas)}
          sub={`${stats.vencidasCount} factura${stats.vencidasCount === 1 ? '' : 's'}`}
          icon={AlertTriangle}
          gradient="bg-gradient-to-br from-rose-50 to-red-50"
          iconBg="bg-gradient-to-br from-rose-500 to-red-500"
          testid="stat-pending-overdue"
        />
        <StatCard
          label="Más vencido"
          value={`${stats.masVencido}d`}
          sub="Antigüedad máxima"
          icon={Wallet}
          gradient="bg-gradient-to-br from-violet-50 to-purple-50"
          iconBg="bg-gradient-to-br from-violet-500 to-purple-500"
          testid="stat-pending-oldest"
        />
      </div>

      {/* Filters */}
      <FiltersCard testid="pending-filters-card">
        <div className="flex flex-col lg:flex-row gap-3">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 pointer-events-none" />
            <Input
              placeholder="Buscar por número o cliente…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-10 h-11 rounded-xl bg-stone-50 border-stone-200 focus:bg-white"
              data-testid="search-pending-input"
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-stone-200 hover:bg-stone-300 flex items-center justify-center text-stone-600">
                <XIcon className="w-3 h-3" />
              </button>
            )}
          </div>
          <div className="relative lg:w-64">
            <Users className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 pointer-events-none z-10" />
            <Select value={clientFilter} onValueChange={setClientFilter}>
              <SelectTrigger className="pl-10 h-11 rounded-xl bg-stone-50 border-stone-200 focus:bg-white" data-testid="pending-client-filter">
                <SelectValue placeholder="Todos los clientes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los clientes</SelectItem>
                {clients.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {activeFiltersCount > 0 && (
            <ResetFiltersButton count={activeFiltersCount} onClick={resetFilters} testid="pending-reset-filters" />
          )}
        </div>

        {years.length > 0 && (
          <FilterRow label="Año" testid="pending-year-filters">
            <FilterChip active={yearFilter === 'all'} onClick={() => setYearFilter('all')} testid="pending-year-chip-all">Todos</FilterChip>
            {years.map((y) => (
              <FilterChip key={y} active={yearFilter === String(y)} onClick={() => setYearFilter(String(y))} testid={`pending-year-chip-${y}`}>{y}</FilterChip>
            ))}
          </FilterRow>
        )}

        <FilterRow label="Trimestre" testid="pending-quarter-filters">
          {QUARTERS.map((q) => (
            <FilterChip key={q.value} active={quarterFilter === q.value} onClick={() => setQuarterFilter(q.value)} testid={`pending-quarter-chip-${q.value}`}>
              {q.label}
            </FilterChip>
          ))}
        </FilterRow>

        <FilterRow label="Estado" testid="pending-status-filters">
          {STATUS_CHIPS.map((s) => (
            <FilterChip key={s.value} active={statusFilter === s.value} onClick={() => setStatusFilter(s.value)} dot={s.dot} testid={`pending-status-chip-${s.value}`}>
              {s.label}
            </FilterChip>
          ))}
        </FilterRow>
      </FiltersCard>

      {/* Bulk actions */}
      {selected.size > 0 && (
        <div className="flex items-center gap-3 bg-stone-50 border border-stone-100 rounded-xl px-4 py-2.5 flex-wrap" data-testid="pending-bulk-bar">
          <span className="text-sm font-medium text-stone-700">{selected.size} seleccionada{selected.size > 1 ? 's' : ''}</span>
          <Button size="sm" className="rounded-xl bg-[#FF6B4A] hover:bg-[#E05A3E]" onClick={handleBulkRemind} disabled={busy} data-testid="pending-bulk-remind">
            <Mail className="w-4 h-4 mr-1.5" />
            {busy ? 'Enviando…' : `Enviar recordatorio (${selected.size})`}
          </Button>
          <Button size="sm" variant="outline" className="rounded-xl border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={handleBulkMarkPaid} disabled={busy} data-testid="pending-bulk-paid">
            <Wallet className="w-4 h-4 mr-1.5" />
            Marcar pagadas
          </Button>
          <Button variant="ghost" size="sm" className="rounded-xl text-stone-500" onClick={() => setSelected(new Set())}>
            Deseleccionar
          </Button>
        </div>
      )}

      {/* Table */}
      {filtered.length > 0 ? (
        <SurfaceCard testid="pending-table-card">
          <div className="overflow-x-auto">
            <table className="w-full invoice-table">
              <thead>
                <tr>
                  <th className="w-10 px-4 py-3">
                    <Checkbox checked={allSelected} onCheckedChange={toggleSelectAll} aria-label="Seleccionar todas" data-testid="pending-select-all" />
                  </th>
                  <th className="text-left px-4 py-3">Número</th>
                  <th className="text-left px-4 py-3">Cliente</th>
                  <th className="text-left px-4 py-3 hidden md:table-cell">Fecha</th>
                  <th className="text-left px-4 py-3 hidden md:table-cell">Vencimiento</th>
                  <th className="text-left px-4 py-3">Estado</th>
                  <th className="text-right px-4 py-3">Total</th>
                  <th className="text-right px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((inv) => {
                  const overdueDays = inv.status === 'overdue' ? daysSince(inv.due_date) : null;
                  return (
                    <tr
                      key={inv.id}
                      className={`hover:bg-orange-50 transition-colors ${selected.has(inv.id) ? 'bg-[#FF6B4A]/5' : ''}`}
                      data-testid={`pending-row-${inv.id}`}
                    >
                      <td className="w-10 px-4 py-4">
                        <Checkbox
                          checked={selected.has(inv.id)}
                          onCheckedChange={() => toggleSelect(inv.id)}
                          aria-label={`Seleccionar ${inv.invoice_number}`}
                          data-testid={`pending-select-${inv.id}`}
                        />
                      </td>
                      <td className="px-4 py-4">
                        <Link to={`/invoices/${inv.id}`} className="font-mono text-sm text-[#FF6B4A] hover:underline">
                          {inv.invoice_number}
                        </Link>
                      </td>
                      <td className="px-4 py-4 text-sm">{inv.client_name}</td>
                      <td className="px-4 py-4 text-sm text-stone-500 hidden md:table-cell">{inv.issue_date}</td>
                      <td className="px-4 py-4 text-sm hidden md:table-cell">
                        <span className={inv.status === 'overdue' ? 'text-red-600 font-semibold' : 'text-stone-500'}>
                          {inv.due_date}
                        </span>
                        {overdueDays != null && overdueDays > 0 && (
                          <span className="ml-2 text-[10px] font-bold text-red-700 bg-red-50 px-1.5 py-0.5 rounded-full">
                            +{overdueDays}d
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${inv.status === 'overdue' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
                          {inv.status === 'overdue' ? 'Vencida' : 'Enviada'}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-right font-mono text-sm font-medium">{fmt(inv.total)}</td>
                      <td className="px-4 py-4 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="p-1.5 hover:bg-stone-100 rounded-xl" data-testid={`pending-menu-${inv.id}`}>
                              <MoreVertical className="w-4 h-4 text-stone-500" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleSendReminder(inv.id)} className="text-blue-700 focus:text-blue-700" data-testid={`pending-remind-${inv.id}`}>
                              <Mail className="w-4 h-4 mr-2" /> Enviar recordatorio
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleMarkPaid(inv.id)} className="text-emerald-700 focus:text-emerald-700" data-testid={`pending-paid-${inv.id}`}>
                              <Wallet className="w-4 h-4 mr-2" /> Marcar como pagada
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => window.open(getInvoicePdfUrl(inv.id), '_blank')}>
                              <Download className="w-4 h-4 mr-2" /> Descargar PDF
                            </DropdownMenuItem>
                            <DropdownMenuItem asChild>
                              <Link to={`/invoices/${inv.id}`} className="flex items-center gap-2">
                                <Send className="w-4 h-4" /> Ver detalle
                              </Link>
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </SurfaceCard>
      ) : (
        <SurfaceCard testid="pending-empty-state">
          <div className="text-center py-12">
            <FileText className="w-12 h-12 mx-auto mb-4 text-stone-300" />
            <h3 className="text-lg font-medium text-stone-900 mb-1">
              {activeFiltersCount > 0 ? 'Sin resultados para los filtros aplicados' : '¡Ninguna factura pendiente!'}
            </h3>
            <p className="text-stone-500 mb-4">
              {activeFiltersCount > 0 ? 'Prueba a limpiar algún filtro.' : 'Todas tus facturas han sido cobradas 🎉'}
            </p>
            {activeFiltersCount > 0 && (
              <button onClick={resetFilters} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-stone-100 hover:bg-stone-200 text-stone-700 font-semibold text-sm transition-all">
                <XIcon className="w-4 h-4" /> Limpiar filtros
              </button>
            )}
          </div>
        </SurfaceCard>
      )}
    </div>
  );
};

export default PendingInvoicesList;
